import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { changePanel, fetchRoofData } from '../RoofDataSlice/roofDataSlice';

type CustomMap = DesignCustomMap;
export type CustomMapStateType = {
	customMaps: CustomMap[];
	editingMapId: number | null;
	isImageEditingEnabled: boolean;
	isCalibrating: boolean;
	originalPixelDistance: number | null;
	uploadingMapIds: number[];
	uploadProgress: number;
	selectedMap?: DesignCustomMap ;
	baselineSelectedMap?: DesignCustomMap;
}

const InitialState: CustomMapStateType = {
	editingMapId: null,
	isImageEditingEnabled: false,
	isCalibrating: false,
	originalPixelDistance: null,
	customMaps: [],
	uploadingMapIds: [],
	uploadProgress: 0
	
};

export const CustomMapSlice = createSlice({
	name: 'customMap',
	initialState: {
		data: InitialState,
	},
	reducers: {
		setEditingMap(state, action: PayloadAction<number | null>) {
			state.data.editingMapId = action.payload ?? null;
			state.data.isImageEditingEnabled = action.payload !== null;
			state.data.isCalibrating = false;
			state.data.originalPixelDistance= null;
			state.data.baselineSelectedMap = state.data.customMaps.find(m=>m.id === action.payload);
		},
		setBaselineMap(state, action: PayloadAction<DesignCustomMap>) {
			state.data.baselineSelectedMap = action.payload;
		},
		toggleImageEditing(state, action: PayloadAction<{ isOpen: boolean }>) {
			state.data.isImageEditingEnabled = action.payload.isOpen;
			if (!action.payload.isOpen) {
				state.data.editingMapId = InitialState.editingMapId;
				state.data.isCalibrating = false;
				state.data.originalPixelDistance = null;
			}
		},
		toggleMapVisibility(state, action: PayloadAction<{ id: number, isVisible: boolean }>) {
			state.data.customMaps = state.data.customMaps.map(m => {
				if (m.id === action.payload.id) {
					m.isVisible = action.payload.isVisible;
				}
				return m;
			});
		},
		editMap(state, action: PayloadAction<{ id: number, editedState: Partial<CustomMap> }>) {
			state.data.customMaps = state.data.customMaps.map(m => {
				if (m.id === action.payload.id) {
					m = { ...m, ...action.payload.editedState };
				}
				return m;
			});
		},
		setIsCalibrating(state, action: PayloadAction<{ enabled: boolean }>) {
			state.data.isCalibrating = action.payload.enabled;
			state.data.originalPixelDistance = null;
		},
		setOriginalDistance(state, action: PayloadAction<{ val: number | null }>) {
			state.data.originalPixelDistance = action.payload.val;
		},
		reorderCustomMaps: (state, action: PayloadAction<Array<{ id: number; sortOrder: number }>>) => {
			const updatedOrder = action.payload;

			// Create a map for quick lookup
			const orderMap = new Map<number, number>();
			updatedOrder.forEach(({ id, sortOrder }) => {
				orderMap.set(id, sortOrder);
			});

			// Update sortOrder
			state.data.customMaps = state.data.customMaps.map((map) => {
				if (orderMap.has(map.id)) {
					map.sortOrder = orderMap.get(map.id)!;
				}
				return map;
			});

		},
		resetCustomMap(state, action: PayloadAction<DesignCustomMap>) {
			state.data.customMaps = state.data.customMaps.map(m =>
				m.id === action.payload.id ? action.payload : m
			);
		},
		addNewCustomMap(state, action: PayloadAction<CustomMap>) {
			state.data.customMaps = [...state.data.customMaps, action.payload];
		},
		removeCustomMap: (state, action: PayloadAction<number>) => {
			state.data.customMaps = state.data.customMaps.filter(m => m.id !== action.payload);
		},
		addUploadingMap: (state, action: PayloadAction<number>) => {
			if (!state.data.uploadingMapIds.includes(action.payload)) {
				state.data.uploadingMapIds = [...state.data.uploadingMapIds, action.payload];
			}
		},
		removeUploadingMap: (state, action: PayloadAction<number>) => {
			state.data.uploadingMapIds = state.data.uploadingMapIds
				.filter(id => id !== action.payload);
			state.data.uploadProgress = InitialState.uploadProgress;

		},
		setUploadProgress(state, action: PayloadAction< number >) {
			state.data.uploadProgress = action.payload;
		},

	}, extraReducers: (builder) => {
		builder.
			addCase(fetchRoofData.fulfilled, (state, action) => {
				state.data.customMaps = action.payload.roofData.customMaps ?? [];
				resetToInitialState(state.data);
			})
			.addCase(changePanel.fulfilled, (state, action) => {
				state.data.customMaps = action.payload.roofData.customMaps ?? [];
				resetToInitialState(state.data);
			});
	}
});

function resetToInitialState(state: CustomMapStateType){
	state.editingMapId = null;
	state.isImageEditingEnabled= false;
	state.isCalibrating= false;
	state.originalPixelDistance= null;
	state.uploadingMapIds= [];
}

export const {
	setEditingMap,
	toggleImageEditing,
	toggleMapVisibility,
	editMap,
	setIsCalibrating,
	setOriginalDistance,
	reorderCustomMaps,
	resetCustomMap,
	addNewCustomMap,
	addUploadingMap,
	removeUploadingMap,
	removeCustomMap,
	setUploadProgress,
	setBaselineMap
}
	= CustomMapSlice.actions;
export default CustomMapSlice.reducer;
